import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Target, TrendingUp, Users, AlertTriangle } from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b'];

const DashboardPage = () => {
  console.log('📊 DashboardPage rendering');
  
  const { user } = useAuth();
  
  // CRITICAL FIX: Extract workspaceId correctly
  const workspaceId = user?.workspaceId || user?.workspace?.id;
  
  console.log('📊 User object:', user);
  console.log('📊 Extracted workspaceId:', workspaceId);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [statusData, setStatusData] = useState([]);
  const [progressDistribution, setProgressDistribution] = useState([]);
  const [topPerformers, setTopPerformers] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [periods, setPeriods] = useState([]);

  // Load periods on mount
  useEffect(() => {
    const loadPeriods = async () => {
      try {
        const response = await api.get('/periods');
        console.log('📅 Periods loaded:', response);
        setPeriods(Array.isArray(response) ? response : (response.data || []));
      } catch (err) {
        console.error('Error loading periods:', err);
      }
    };
    loadPeriods();
  }, []);

  // Load dashboard data
  useEffect(() => {
  const loadDashboard = async () => {
    if (!workspaceId) {
      console.log('⚠️ No workspaceId available');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      console.log('📊 Loading dashboard for workspace:', workspaceId);
      console.log('📊 Selected period:', selectedPeriod);
      
      const params = selectedPeriod !== 'all' ? { period: selectedPeriod } : {};
      
      const [analyticsRes, statusRes, distributionRes, performersRes] = await Promise.all([
        api.get(`/workspaces/${workspaceId}/analytics`, { params }),
        api.get(`/workspaces/${workspaceId}/objectives-by-status`),
        api.get(`/workspaces/${workspaceId}/progress-distribution`),
        api.get(`/workspaces/${workspaceId}/top-performers`, { params })
      ]);
      
      console.log('✅ Analytics response:', analyticsRes);
      console.log('✅ Status response:', statusRes);
      console.log('✅ Distribution response:', distributionRes);
      console.log('✅ Performers response:', performersRes);
      
      setDashboardData(analyticsRes);
      setStatusData(Array.isArray(statusRes) ? statusRes : []);
      setProgressDistribution(Array.isArray(distributionRes) ? distributionRes : []);
      setTopPerformers(Array.isArray(performersRes) ? performersRes : []);
      
      console.log('✅ All dashboard data set');
      
    } catch (err) {
      console.error('❌ Dashboard error:', err);
      setError(err.response?.data?.error || 'Error al cargar el dashboard');
    } finally {
      setLoading(false);
    }
  };

  loadDashboard();
}, [workspaceId, selectedPeriod]);

  if (loading) {
    console.log('📊 Dashboard loading state');
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-500">Cargando dashboard...</div>
      </div>
    );
  }

  if (error) {
    console.error('📊 Dashboard error:', error);
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <p className="text-red-800 font-semibold">Error al cargar el dashboard</p>
        <p className="text-red-600 text-sm mt-2">{error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Reintentar
        </button>
      </div>
    );
  }

  if (!dashboardData) {
    console.log('📊 Dashboard no data');
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No hay datos disponibles</p>
        <button 
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
        >
          Recargar
        </button>
      </div>
    );
  }

  console.log('📊 Dashboard rendering with data:', dashboardData);

  return (
    <div className="space-y-6">
      {/* Header with Period Selector */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
        >
          <option value="all">Todos los periodos</option>
          {periods.map(period => (
            <option key={period.id} value={period.id}>
              {period.label}
            </option>
          ))}
        </select>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Objetivos</p>
              <p className="text-3xl font-bold mt-2">
                {dashboardData.overall?.total_objectives ?? 0}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Progreso: {dashboardData.overall?.avg_progress ?? 0}%
              </p>
            </div>
            <Target className="w-12 h-12 text-indigo-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Key Results</p>
              <p className="text-3xl font-bold mt-2">
                {dashboardData.overall?.total_key_results ?? 0}
              </p>
            </div>
            <TrendingUp className="w-12 h-12 text-indigo-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Miembros del Equipo</p>
              <p className="text-3xl font-bold mt-2">
                {dashboardData.overall?.team_members ?? 0}
              </p>
            </div>
            <Users className="w-12 h-12 text-indigo-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">OKRs en Riesgo</p>
              <p className="text-3xl font-bold mt-2 text-red-600">
                {dashboardData.atRisk ?? 0}
              </p>
              <p className="text-sm text-gray-500 mt-1">Requieren atención</p>
            </div>
            <AlertTriangle className="w-12 h-12 text-red-600" />
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Objectives by Status */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Objetivos por Estado</h3>
          {statusData && statusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={statusData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="status" 
                  style={{ fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}
                />
                <YAxis 
                  style={{ fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    fontSize: '13px', 
                    fontFamily: 'Inter, system-ui, sans-serif'
                  }}
                />
                <Legend 
                  wrapperStyle={{ 
                    fontSize: '13px', 
                    fontFamily: 'Inter, system-ui, sans-serif' 
                  }}
                />
                <Bar dataKey="count" fill="#6366f1" name="Objetivos" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <p>No hay datos disponibles</p>
            </div>
          )}
        </div>

        {/* Progress Distribution */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Distribución de Progreso</h3>
          {progressDistribution && progressDistribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={progressDistribution}
                  dataKey="count"
                  nameKey="range"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label
                >
                  {progressDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    fontSize: '13px', 
                    fontFamily: 'Inter, system-ui, sans-serif' 
                  }}
                />
                <Legend 
                  wrapperStyle={{ 
                    fontSize: '13px', 
                    fontFamily: 'Inter, system-ui, sans-serif' 
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <p>No hay datos disponibles</p>
            </div>
          )}
        </div>
      </div>

      {/* Progress Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Progreso: Actual vs Esperado</h3>
        {dashboardData.currentWeek ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={Array.from({ length: 17 }, (_, i) => ({
                week: i + 1,
                actual: i + 1 <= dashboardData.currentWeek ? 
                  (parseFloat(dashboardData.overall?.avg_progress || 0) * (i + 1)) / dashboardData.currentWeek : null,
                expected: ((i + 1) / 17) * 100
              }))}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="week" 
                label={{ value: 'Semana', position: 'insideBottom', offset: -5 }}
                style={{ fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}
              />
              <YAxis 
                label={{ value: 'Progreso (%)', angle: -90, position: 'insideLeft' }}
                style={{ fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}
              />
              <Tooltip 
                contentStyle={{ 
                  fontSize: '13px', 
                  fontFamily: 'Inter, system-ui, sans-serif' 
                }}
                formatter={(value) => {
                  if (value === null || value === undefined) return 'N/A';
                  return `${parseFloat(value).toFixed(1)}%`;
                }}
              />
              <Legend 
                wrapperStyle={{ 
                  fontSize: '13px', 
                  fontFamily: 'Inter, system-ui, sans-serif' 
                }}
              />
              <Line 
                type="monotone" 
                dataKey="actual" 
                stroke="#6366f1" 
                strokeWidth={2}
                name="Progreso Actual" 
              />
              <Line 
                type="monotone" 
                dataKey="expected" 
                stroke="#94a3b8" 
                strokeWidth={2}
                strokeDasharray="5 5"
                name="Progreso Esperado" 
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <p>No hay datos disponibles</p>
          </div>
        )}
      </div>

      {/* Top Performers */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Top Performers</h3>
        {topPerformers && topPerformers.length > 0 ? (
          <div className="space-y-3">
            {topPerformers.map((performer, index) => (
              <div key={performer.id} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                    <span className="text-sm font-semibold text-indigo-600">
                      #{index + 1}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {performer.full_name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {performer.updates_count} actualizaciones
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-indigo-600">
                    {parseFloat(performer.avg_progress || 0).toFixed(0)}%
                  </p>
                  <p className="text-xs text-gray-500">
                    {performer.krs_owned} KRs
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500 text-sm">
              No hay actividad registrada aún
            </p>
            <p className="text-gray-400 text-xs mt-1">
              Los usuarios aparecerán aquí al registrar actualizaciones
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
